import type { CopilotChatPanelProps } from "@zapier/ai-toolkit/client";
import type { Message } from "ai";

export const COPILOT_PROJECT_NAME = "interfaces";
export const COPILOT_REGISTRY_ID = "interfaces";
export const COPILOT_API_ROUTE_PATH = "/api/interfaces/copilot";

/**
 * See https://ai-toolkit.vercel.zapier.com/docs/observability
 */
export const COPILOT_FEEDBACK_API_ROUTE_PATH =
  "/api/interfaces/copilot/feedback";

export const COPILOT_LOAD_MESSAGES_API_ROUTE_PATH =
  /**
   * The ai-toolkit will append the `/${conversationId}/messages` to this path.
   */
  "/api/interfaces/copilot/conversations";

/**
 * The number is arbitrary.
 */
export const COPILOT_CLIENT_TOOLS_MAX_STEPS = 10;

export const COPILOT_CONVERSATION_ID_QUERY_PARAM = "conversationId";

export const COPILOT_NEW_INTERFACE_NAME_QUERY_PARAM = "name";

/**
 * The default name is arbitrary.
 */
export const COPILOT_NEW_INTERFACE_DEFAULT_PROJECT_NAME = "New Interface";

/**
 * The default name is arbitrary.
 */
export const COPILOT_NEW_INTERFACE_DEFAULT_PAGE_NAME = "Page";

export const COPILOT_NEW_FROM_COPILOT_ROUTE_PATHNAME =
  "/interfaces/new-from-copilot";

export const COPILOT_TOOL_NAMES = {
  ADD_BLOCKS: "addBlocks",
  CONFIGURE_NAVIGATION: "configureNavigation",
  CREATE_PAGES: "createPages",
  CREATE_TABLE_FIELDS: "createTableFields",
  CREATE_TABLE_RECORDS: "createTableRecords",
  CREATE_TABLES_WITH_FIELDS_AND_RECORDS: "createTablesWithFieldsAndRecords",
  CREATE_ZAPS: "createZaps",
  GET_TABLES_FIELDS: "getTablesFields",
  SEARCH_HELP_DOCS: "searchHelpDocs",
  SEARCH_TABLES_BY_NAME: "searchTablesByName",
  TABLE_PICKER: "tablePicker",
  UPDATE_BLOCK: "updateBlock",
  UPDATE_CUSTOM_DOMAIN: "updateCustomDomain",
  UPDATE_FOCUS: "updateFocus",
  UPDATE_INTERFACE: "updateInterface",
  UPDATE_INTERFACE_SLUG: "updateInterfaceSlug",
  UPDATE_PAGE: "updatePage",
  GET_THEME: "getTheme",
  UPDATE_THEME: "updateTheme",
  UPSERT_AI_GENERATED_COMPONENT: "upsertAIGeneratedComponent",
  GET_CONNECTED_ASSETS: "getConnectedAssets",
} as const;

export type CopilotToolName =
  (typeof COPILOT_TOOL_NAMES)[keyof typeof COPILOT_TOOL_NAMES];

/**
 * 1. Block => Component to match our UI.
 * 2. We want to keep the display names succinct and to the point.
 */
export const COPILOT_TOOL_DISPLAY_NAMES = {
  addBlocks: "Add Components",
  configureNavigation: "Configure Navigation",
  createPages: "Create Pages",
  createTableFields: "Create Table Fields",
  createTableRecords: "Create Table Records",
  createTablesWithFieldsAndRecords: "Create Tables",
  createZaps: "Create Zaps",
  getTablesFields: "Get Tables Fields",
  searchHelpDocs: "Search Help Docs",
  searchTablesByName: "Search Tables",
  tablePicker: "Pick a Table",
  updateBlock: "Update Component",
  updateCustomDomain: "Update Custom Domain",
  updateFocus: "Update Focus",
  updateInterface: "Update Interface",
  updateInterfaceSlug: "Update Interface Slug",
  updatePage: "Update Page",
  getTheme: "Get Theme",
  updateTheme: "Update Theme",
  upsertAIGeneratedComponent: "Upsert AI Generated Component",
  getConnectedAssets: "Get Connected Assets",
} satisfies Record<CopilotToolName, string>;

export const COPILOT_INITIAL_MESSAGE: Message = {
  id: "welcome",
  role: "assistant",
  content:
    "Hi! I'm your Copilot for building Interfaces that work beautifully with your automations. I can help you:\n\n🧱 Build a complete Interface from scratch\n\n🛠️ Configure a complex form\n\n🧩 Create custom components tailored to your use case\n\n🔗 Connect components to Zaps and Tables so your app runs on live automation data\n\nWhat do you want to build today?",
  createdAt: new Date(),
};

/**
 * See https://zapier.slack.com/archives/C048W40M39S/p1755608847606959?thread_ts=1755523909.790369&cid=C048W40M39S
 * The `prompt` property was auto-generated by the Interfaces Copilot given the `displayText`.
 */
export const COPILOT_EMPTY_STATE_SUGGESTIONS: CopilotChatPanelProps["emptyStateSuggestions"] =
  [
    {
      displayText: "Lead intake form",
      iconName: "personGroup",
      prompt:
        "Create a lead capture form with name, email, company, and phone fields. Store submissions in a new Zapier Table and create a Zap which sends me email notifications when form is submitted.",
    },
    {
      displayText: "File upload form",
      iconName: "fileAdd",
      prompt:
        "Build a file upload form for document submissions with name, email, and file upload fields. Save submissions to a Zapier Table and create a Zap which triggers when form is submitted.",
    },
    {
      displayText: "Product feedback form",
      iconName: "ratingStar",
      prompt:
        "Create a feedback form with product rating, comments, and contact fields. Store responses in a Zapier Table and set up a Zap which triggers follow-up actions based on ratings when form is submitted.",
    },
    {
      displayText: "Invoice form",
      iconName: "miscWallet",
      prompt:
        "Build an invoice request form with client info, project details, and amount fields. Save to a Zapier Table and create a Zap which processes new invoice requests when form is submitted.",
    },
  ];

/**
 * A constant, so we can create a metric in Datadog out of this exact message.
 */
export const COPILOT_SERVER_TOOL_CALL_FAILURE_LOG_MESSAGE =
  "Server tool call failed";

/**
 * A constant, so we can create a metric in Datadog out of this exact message.
 */
export const COPILOT_STREAM_ERROR_LOG_MESSAGE = "Copilot stream error";
